export interface HeroCard {
  id: string;
  kicker: string;
  title: string;
  desc: string;
  image: string;
  meta: string;
  tone: "primary" | "accent" | "secondary";
}

export interface NavItem {
  label: string;
  href: string;
}

export interface IndexTopic {
  id: string;
  no: string;
  title: string;
  count: string;
  tag: string;
  image: string;
}

export interface FeedItem {
  id: string;
  category: string;
  title: string;
  summary: string;
  image: string;
  date: string;
  views: string;
  tags: string[];
}

export interface EvidenceItem {
  id: string;
  label: string;
  title: string;
  desc: string;
  image: string;
  verified: boolean;
}

export interface ResourceItem {
  id: string;
  title: string;
  format: string;
  size: string;
  quality: string;
  updated: string;
}

export interface GalleryItem {
  id: string;
  title: string;
  image: string;
  count: string;
}

export interface UpdateItem {
  id: string;
  time: string;
  title: string;
  type: string;
}

export const navLinks: NavItem[] = [
  { label: "首页", href: "#top" },
  { label: "合集索引", href: "#index" },
  { label: "大图流", href: "#feed" },
  { label: "实锤证据", href: "#evidence" },
  { label: "资源下载", href: "#resource" },
  { label: "高清图集", href: "#gallery" },
  { label: "更新动态", href: "#updates" },
];

export const tickerItems: string[] = [
  "今日新增视频合集 26 组，图片素材 480 张",
  "「深夜录音门」实锤档案已补齐时间线",
  "高清原图包支持未压缩直接下载",
  "证据索引新增按时间、按来源两种排序",
];

export const heroCards: HeroCard[] = [
  {
    id: "hero-1",
    kicker: "本周主推合集",
    title: "夜场纪实 · 全程高清收录",
    desc: "从入场到离场的关键画面按时间排好，视频与图片同步归档，一屏就能看完整个过程。",
    image:
      "https://readdy.ai/api/search-image?query=Moody%20cinematic%20night%20cityscape%20with%20neon%20reflections%20on%20rain-soaked%20asphalt%2C%20warm%20amber%20and%20deep%20crimson%20color%20grading%2C%20editorial%20magazine%20atmosphere%2C%20heavy%20film%20grain%2C%20no%20people%2C%20high%20contrast%20dramatic%20lighting%2C%20abstract%20urban%20texture%2C%20premium%20quality&width=1200&height=800&seq=hero-night-city-01&orientation=landscape",
    meta: "视频 12 段 · 图片 186 张",
    tone: "primary",
  },
  {
    id: "hero-2",
    kicker: "实锤更新",
    title: "证据材料一期已整理成册",
    desc: "聊天记录、时间节点、票据凭证按类别归位，可逐条核对。",
    image:
      "https://readdy.ai/api/search-image?query=Close-up%20abstract%20of%20stacked%20documents%20and%20folders%20on%20a%20dark%20wooden%20desk%20under%20warm%20amber%20lamp%20light%2C%20mysterious%20investigative%20editorial%20mood%2C%20crimson%20accents%2C%20shallow%20depth%20of%20field%2C%20film%20grain%2C%20no%20readable%20text%2C%20no%20people%2C%20cinematic%20still%20life&width=700&height=480&seq=hero-documents-03&orientation=landscape",
    meta: "凭证 42 项 · 已标注",
    tone: "accent",
  },
  {
    id: "hero-3",
    kicker: "高清图集",
    title: "现场机位 · 未裁剪原图",
    desc: "保留完整构图的现场大图，按机位分类，适合快速翻阅比对。",
    image:
      "https://readdy.ai/api/search-image?query=Abstract%20dramatic%20spotlight%20beam%20cutting%20through%20smoky%20dark%20studio%20air%2C%20warm%20golden%20and%20crimson%20tones%2C%20theatrical%20stage%20atmosphere%2C%20film%20grain%2C%20no%20people%2C%20minimal%20editorial%20composition%2C%20elegant%20negative%20space%2C%20cinematic%20mood&width=700&height=480&seq=hero-spotlight-02&orientation=landscape",
    meta: "图集 9 套 · 高清",
    tone: "secondary",
  },
];

export const indexTopics: IndexTopic[] = [
  {
    id: "idx-1",
    no: "01",
    title: "深夜录音门 · 完整音频与文字稿",
    count: "收录 18 段",
    tag: "音频实锤",
    image:
      "https://readdy.ai/api/search-image?query=Vintage%20microphone%20in%20a%20smoky%20dim%20studio%20with%20warm%20amber%20rim%20light%20and%20deep%20red%20background%20glow%2C%20cinematic%20editorial%20still%20life%2C%20film%20grain%2C%20mysterious%20mood%2C%20no%20people%2C%20high%20contrast%2C%20premium%20magazine%20quality&width=500&height=500&seq=idx-mic-04&orientation=squarish",
  },
  {
    id: "idx-2",
    no: "02",
    title: "跟拍机位合集 · 长焦直出未删减",
    count: "收录 34 组",
    tag: "视频合集",
    image:
      "https://readdy.ai/api/search-image?query=Professional%20camera%20with%20telephoto%20lens%20on%20a%20dark%20table%20under%20warm%20golden%20light%2C%20crimson%20backdrop%2C%20investigative%20photojournalism%20mood%2C%20film%20grain%2C%20shallow%20depth%20of%20field%2C%20no%20people%2C%20cinematic%20still%20life&width=500&height=500&seq=idx-camera-05&orientation=squarish",
  },
  {
    id: "idx-3",
    no: "03",
    title: "往来信件与凭证 · 逐张扫描件",
    count: "收录 56 张",
    tag: "书面证据",
    image:
      "https://readdy.ai/api/search-image?query=Sealed%20envelope%20and%20red%20wax%20stamp%20on%20dark%20textured%20paper%20under%20warm%20dramatic%20side%20light%2C%20mysterious%20editorial%20atmosphere%2C%20crimson%20and%20amber%20tones%2C%20film%20grain%2C%20no%20readable%20text%2C%20no%20people%2C%20cinematic%20still%20life&width=500&height=500&seq=idx-envelope-06&orientation=squarish",
  },
  {
    id: "idx-4",
    no: "04",
    title: "线上痕迹归档 · 时间线还原",
    count: "收录 22 条",
    tag: "电子证据",
    image:
      "https://readdy.ai/api/search-image?query=Close-up%20of%20a%20laptop%20keyboard%20glowing%20in%20a%20dark%20room%20with%20warm%20amber%20and%20deep%20crimson%20reflections%2C%20abstract%20investigative%20editorial%20mood%2C%20heavy%20film%20grain%2C%20no%20readable%20screen%20content%2C%20no%20people%2C%20cinematic&width=500&height=500&seq=idx-keyboard-07&orientation=squarish",
  },
];

export const feedItems: FeedItem[] = [
  {
    id: "feed-1",
    category: "视频合集",
    title: "后台通道全程记录：进场与撤场的关键节点",
    summary: "按时间轴剪好的完整版，标记了三个被反复讨论的时间节点，方便直接跳到想核对的片段。",
    image:
      "https://readdy.ai/api/search-image?query=Warm%20lit%20concert%20stage%20seen%20from%20backstage%20with%20deep%20crimson%20curtains%20and%20golden%20spotlights%2C%20dramatic%20editorial%20atmosphere%2C%20abstract%2C%20no%20people%2C%20heavy%20film%20grain%2C%20cinematic%20wide%20shot%2C%20premium%20magazine%20quality&width=900&height=620&seq=feed-stage-08&orientation=landscape",
    date: "09-20",
    views: "3.2万",
    tags: ["无删减", "时间轴", "高清"],
  },
  {
    id: "feed-2",
    category: "图片专题",
    title: "雨夜街巷机位：连拍 120 张的高清原图",
    summary: "同一位置不同角度的连续拍摄，保留了环境参照物，便于比对画面中的细节差异。",
    image:
      "https://readdy.ai/api/search-image?query=Rainy%20narrow%20city%20alley%20at%20night%20glowing%20with%20warm%20amber%20street%20lamps%20and%20crimson%20neon%20signage%2C%20moody%20cinematic%20editorial%20photograph%2C%20wet%20reflections%2C%20film%20grain%2C%20no%20people%2C%20abstract%20urban%20atmosphere%2C%20high%20contrast&width=900&height=620&seq=feed-alley-09&orientation=landscape",
    date: "09-19",
    views: "1.8万",
    tags: ["连拍", "原图", "机位"],
  },
  {
    id: "feed-3",
    category: "实锤档案",
    title: "演播厅空镜与时间戳：核对录制顺序",
    summary: "以现场时钟与灯光变化为参照，整理出可复核的录制顺序，避免片段被误读。",
    image:
      "https://readdy.ai/api/search-image?query=Dim%20television%20studio%20set%20with%20empty%20chairs%20and%20warm%20golden%20practical%20lights%20against%20deep%20crimson%20walls%2C%20cinematic%20editorial%20atmosphere%2C%20mysterious%20mood%2C%20film%20grain%2C%20no%20people%2C%20abstract%20composition%2C%20premium%20quality&width=900&height=620&seq=feed-studio-10&orientation=landscape",
    date: "09-18",
    views: "2.6万",
    tags: ["时间戳", "可核对"],
  },
  {
    id: "feed-4",
    category: "视频合集",
    title: "车内视角片段：夜间行驶全过程",
    summary: "收录完整夜间行驶片段，画面稳定、车牌与环境标识清晰，适合作为行程时间参照。",
    image:
      "https://readdy.ai/api/search-image?query=Luxury%20car%20steering%20wheel%20and%20dashboard%20at%20night%20lit%20by%20warm%20amber%20city%20light%20with%20deep%20crimson%20reflections%2C%20cinematic%20editorial%20mood%2C%20film%20grain%2C%20no%20people%2C%20abstract%20high%20contrast%20composition%2C%20premium%20magazine%20quality&width=900&height=620&seq=feed-wheel-11&orientation=landscape",
    date: "09-17",
    views: "4.1万",
    tags: ["完整版", "夜间"],
  },
  {
    id: "feed-5",
    category: "图片专题",
    title: "走廊与电梯间：公共区域画面归档",
    summary: "按楼层与时间分批归档的公共区域画面，标注了摄像头的可见范围与遮挡情况。",
    image:
      "https://readdy.ai/api/search-image?query=Empty%20elegant%20hotel%20corridor%20at%20night%20with%20warm%20golden%20wall%20sconces%20and%20deep%20crimson%20carpet%2C%20mysterious%20cinematic%20editorial%20atmosphere%2C%20film%20grain%2C%20no%20people%2C%20symmetrical%20abstract%20composition%2C%20premium%20quality&width=900&height=620&seq=feed-hotel-12&orientation=landscape",
    date: "09-16",
    views: "1.5万",
    tags: ["按楼层", "归档"],
  },
  {
    id: "feed-6",
    category: "实锤档案",
    title: "屏幕截图取证：界面元素与版本信息",
    summary: "保留完整界面与版本信息的截图集合，方便核对内容产生的时间与来源。",
    image:
      "https://readdy.ai/api/search-image?query=Smartphone%20screen%20glowing%20in%20a%20dark%20room%20casting%20warm%20amber%20and%20crimson%20light%20on%20a%20textured%20surface%2C%20abstract%20investigative%20editorial%20mood%2C%20film%20grain%2C%20no%20readable%20content%2C%20no%20people%2C%20cinematic%20still%20life&width=900&height=620&seq=feed-phonelight-13&orientation=landscape",
    date: "09-15",
    views: "2.0万",
    tags: ["截图", "取证"],
  },
];

export const evidenceItems: EvidenceItem[] = [
  {
    id: "ev-1",
    label: "书面材料",
    title: "打印稿与手写标注对照",
    desc: "保留原始打印版本与批注位置，标注处可放大查看细节。",
    image:
      "https://readdy.ai/api/search-image?query=Neatly%20arranged%20pile%20of%20printed%20documents%20with%20red%20marker%20highlights%20on%20a%20dark%20desk%2C%20warm%20amber%20desk%20lamp%20light%2C%20investigative%20editorial%20still%20life%2C%20film%20grain%2C%20no%20readable%20text%2C%20no%20people%2C%20cinematic%20high%20contrast&width=600&height=600&seq=ev-papers-14&orientation=squarish",
    verified: true,
  },
  {
    id: "ev-2",
    label: "消费凭证",
    title: "票据与流水时间对照",
    desc: "以时间为轴排列的消费记录，方便与行程节点互相印证。",
    image:
      "https://readdy.ai/api/search-image?query=Crumpled%20receipts%20and%20paper%20slips%20scattered%20on%20a%20dark%20wooden%20surface%20under%20warm%20golden%20light%2C%20crimson%20accent%2C%20investigative%20editorial%20mood%2C%20film%20grain%2C%20no%20readable%20text%2C%20no%20people%2C%20cinematic%20still%20life%2C%20premium%20quality&width=600&height=600&seq=ev-receipt-15&orientation=squarish",
    verified: true,
  },
  {
    id: "ev-3",
    label: "音视频原件",
    title: "原始录音与影像留存",
    desc: "未经二次压缩的原始文件，保留完整时长与文件信息。",
    image:
      "https://readdy.ai/api/search-image?query=Old%20cassette%20tape%20and%20audio%20reel%20on%20a%20dark%20textured%20table%20lit%20by%20warm%20amber%20light%20with%20deep%20crimson%20glow%2C%20nostalgic%20investigative%20editorial%20atmosphere%2C%20film%20grain%2C%20no%20people%2C%20cinematic%20still%20life&width=600&height=600&seq=ev-tape-16&orientation=squarish",
    verified: false,
  },
];

export const resourceItems: ResourceItem[] = [
  {
    id: "res-1",
    title: "夜场纪实 视频原档合辑",
    format: "MP4",
    size: "3.6 GB",
    quality: "4K 原画",
    updated: "09-20 更新",
  },
  {
    id: "res-2",
    title: "现场机位 未裁剪图片包",
    format: "ZIP",
    size: "1.2 GB",
    quality: "原图无损",
    updated: "09-19 更新",
  },
  {
    id: "res-3",
    title: "实锤证据 扫描件与对照表",
    format: "PDF",
    size: "86 MB",
    quality: "300 DPI",
    updated: "09-18 更新",
  },
  {
    id: "res-4",
    title: "录音门 完整音频与字幕",
    format: "WAV",
    size: "640 MB",
    quality: "无损音质",
    updated: "09-17 更新",
  },
];

export const galleryItems: GalleryItem[] = [
  {
    id: "gal-1",
    title: "闪光灯下的暗场",
    count: "48 张",
    image:
      "https://readdy.ai/api/search-image?query=Abstract%20blurred%20camera%20flashes%20in%20a%20dark%20crowd-free%20space%2C%20warm%20golden%20bokeh%20and%20deep%20crimson%20tones%2C%20cinematic%20editorial%20atmosphere%2C%20film%20grain%2C%20no%20people%2C%20premium%20quality&width=500&height=500&seq=gal-flash-17&orientation=squarish",
  },
  {
    id: "gal-2",
    title: "镜面与帷幔",
    count: "32 张",
    image:
      "https://readdy.ai/api/search-image?query=Antique%20mirror%20reflecting%20warm%20amber%20light%20in%20a%20dark%20moody%20room%2C%20deep%20crimson%20drapery%2C%20mysterious%20editorial%20still%20life%2C%20film%20grain%2C%20no%20people%2C%20abstract%20composition%2C%20cinematic%20quality&width=500&height=500&seq=gal-mirror-18&orientation=squarish",
  },
  {
    id: "gal-3",
    title: "包厢桌上的细节",
    count: "56 张",
    image:
      "https://readdy.ai/api/search-image?query=Champagne%20glass%20and%20ice%20bucket%20on%20a%20dark%20table%20under%20warm%20golden%20club%20lighting%20with%20deep%20crimson%20backdrop%2C%20luxurious%20editorial%20still%20life%2C%20film%20grain%2C%20no%20people%2C%20cinematic%20high%20contrast%2C%20premium%20quality&width=500&height=500&seq=gal-champagne-19&orientation=squarish",
  },
  {
    id: "gal-4",
    title: "多屏同录画面",
    count: "24 张",
    image:
      "https://readdy.ai/api/search-image?query=Multiple%20glowing%20television%20screens%20stacked%20in%20a%20dark%20room%20showing%20abstract%20warm%20light%2C%20crimson%20and%20amber%20reflections%2C%20editorial%20investigative%20mood%2C%20film%20grain%2C%20no%20readable%20content%2C%20no%20people%2C%20cinematic&width=500&height=500&seq=gal-screen-20&orientation=squarish",
  },
  {
    id: "gal-5",
    title: "清晨散场路线",
    count: "40 张",
    image:
      "https://readdy.ai/api/search-image?query=Empty%20city%20street%20at%20dawn%20with%20warm%20golden%20sunlight%20and%20long%20crimson%20shadows%2C%20cinematic%20editorial%20photograph%2C%20film%20grain%2C%20no%20people%2C%20abstract%20urban%20atmosphere%2C%20premium%20magazine%20quality&width=500&height=500&seq=gal-street-21&orientation=squarish",
  },
  {
    id: "gal-6",
    title: "幕布缝隙里的光",
    count: "18 张",
    image:
      "https://readdy.ai/api/search-image?query=Heavy%20deep%20crimson%20velvet%20curtain%20backstage%20with%20a%20slit%20of%20warm%20golden%20light%2C%20theatrical%20editorial%20atmosphere%2C%20film%20grain%2C%20no%20people%2C%20abstract%20dramatic%20composition%2C%20cinematic%20premium%20quality&width=500&height=500&seq=gal-curtain-22&orientation=squarish",
  },
];

export const updateItems: UpdateItem[] = [
  {
    id: "up-1",
    time: "今天 09:40",
    title: "「夜场纪实」新增 12 段视频与 186 张原图",
    type: "视频",
  },
  {
    id: "up-2",
    time: "昨天 21:15",
    title: "实锤档案补入 42 项凭证与时间线对照表",
    type: "证据",
  },
  {
    id: "up-3",
    time: "09-18 14:02",
    title: "高清图集上新 3 套，机位与原图均已归位",
    type: "图集",
  },
  {
    id: "up-4",
    time: "09-17 10:26",
    title: "资源下载区开放无损音频与 4K 原档入口",
    type: "资源",
  },
];

export const shellStats = [
  { label: "合集总数", value: "1,286" },
  { label: "视频素材", value: "9,540" },
  { label: "高清图片", value: "42,180" },
  { label: "今日更新", value: "26" },
];